import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { settingsAPI, contactAPI } from '../utils/api';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    settingsAPI.get()
      .then((res) => setSettings(res.data))
      .catch((err) => console.error('Failed to load settings', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await contactAPI.submit(formData);
      toast.success('Zpráva byla odeslána! Brzy vás kontaktujeme.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Chyba při odesílání');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div 
        className="relative py-20"
      >
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Kontaktujte nás</h1>
          <p className="text-xl text-white/90">
            Máte otázky? Rádi vám pomůžeme
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="shadow-lg" data-testid="contact-email-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Email</h3>
                    <p className="text-gray-600" data-testid="contact-email-primary">
                      {settings?.contact_email_primary || 'info@wildcamping.cz'}
                    </p>
                    {settings?.contact_email_support && (
                      <p className="text-gray-600" data-testid="contact-email-support">
                        {settings.contact_email_support}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg" data-testid="contact-phone-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Telefon</h3>
                    <p className="text-gray-600" data-testid="contact-phone">
                      {settings?.contact_phone || '+420 123 456 789'}
                    </p>
                    {settings?.contact_phone_hours && (
                      <p className="text-sm text-gray-500 mt-1" data-testid="contact-phone-hours">
                        {settings.contact_phone_hours}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg" data-testid="contact-address-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Adresa</h3>
                    <p className="text-gray-600" data-testid="contact-address">
                      {settings?.address_company || 'WildCamping s.r.o.'}<br />
                      {settings?.address_street || 'Přírodní 123'}<br />
                      {settings?.address_city || '602 00 Brno'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg bg-orange-50 border-orange-200" data-testid="contact-hours-card">
              <CardContent className="p-6">
                <h3 className="font-bold mb-2">Pracovní doba</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>Pondělí - Pátek:</span>
                    <span className="font-semibold" data-testid="hours-weekdays">
                      {settings?.hours_weekdays || '9:00 - 18:00'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sobota:</span>
                    <span className="font-semibold" data-testid="hours-saturday">
                      {settings?.hours_saturday || '10:00 - 15:00'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Neděle:</span>
                    <span className="font-semibold" data-testid="hours-sunday">
                      {settings?.hours_sunday || 'Zavřeno'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6">Napište nám</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Jméno</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Vaše jméno"
                        className="mt-1"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="vas@email.cz"
                        className="mt-1"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Předmět</Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="O čem chcete psát?"
                      className="mt-1"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Zpráva</Label>
                    <Textarea
                      id="message"
                      placeholder="Napište nám vaši zprávu..."
                      rows={6}
                      className="mt-1"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg"
                    data-testid="contact-submit-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {submitting ? 'Odesílám...' : 'Odeslat zprávu'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* FAQ Quick Links */}
            <Card className="shadow-lg mt-8">
              <CardContent className="p-6">
                <h3 className="font-bold mb-4">Častokládané otázky</h3>
                <p className="text-gray-600 mb-4">
                  Než nám napíšete, podívejte se na naše často kladené otázky. Možná tam najdete odpověď na váš dotaz.
                </p>
                <Button variant="outline" className="w-full" onClick={() => window.location.href = '/how-it-works'}>
                  Zobrazit FAQ
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
